import React, { useMemo, useState } from 'react';
import { Button, Divider, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import type { SelectProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useCreatePaymentTerm, usePaymentTermsForSelection } from '@/hooks/usePaymentTerms';
import type { PaymentTerm, PaymentTermSelectionItem } from '@/services/paymentTerm.service';

const { Text } = Typography;

function documentText(term: { term_name: string; description?: string | null }): string {
  return (term.description ?? '').trim() || term.term_name.trim();
}

export type PaymentTermSelectProps = Omit<SelectProps, 'options' | 'mode' | 'popupRender'> & {
  /** 'documentText' (default) stores description-or-name for documents; 'name' stores the term name (masters). */
  storeAs?: 'documentText' | 'name';
  /** Called with the selected master term (undefined when cleared or a non-master value). */
  onTermSelected?: (term: PaymentTermSelectionItem | undefined) => void;
};

/**
 * Finance Payment Terms picker. Stores document text (description or name) unless storeAs="name".
 * Footer lets the user add a missing term to the company master.
 */
export const PaymentTermSelect: React.FC<PaymentTermSelectProps> = ({
  value,
  onChange,
  disabled,
  placeholder = 'Select payment terms',
  storeAs = 'documentText',
  onTermSelected,
  ...rest
}) => {
  const storedValue = (term: { term_name: string; description?: string | null }) =>
    storeAs === 'name' ? term.term_name.trim() : documentText(term);
  const { data: master = [] } = usePaymentTermsForSelection();
  const createMutation = useCreatePaymentTerm();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [createForm] = Form.useForm();

  const options = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    const add = (nextValue: string, label: string) => {
      const v = nextValue.trim();
      if (!v || seen.has(v)) return;
      seen.add(v);
      opts.push({ value: v, label });
    };
    for (const t of master) {
      add(storedValue(t), t.term_name);
    }
    const current = String(value ?? '').trim();
    if (current) add(current, current);
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master, value, storeAs]);

  const handleChange: SelectProps['onChange'] = (next, option) => {
    onChange?.(next, option);
    onTermSelected?.(master.find((t) => storedValue(t) === next));
  };

  const openCreate = (presetName?: string) => {
    createForm.resetFields();
    createForm.setFieldsValue({
      term_name: String(presetName || search || '').trim().slice(0, 100),
      days: 0,
      description: '',
    });
    setModalOpen(true);
  };

  const handleCreate = async () => {
    const values = await createForm.validateFields();
    const created: PaymentTerm = await createMutation.mutateAsync({
      term_name: String(values.term_name).trim(),
      days: values.days ?? 0,
      description: String(values.description || '').trim() || undefined,
      is_active: true,
    });
    const next = storedValue(created);
    onChange?.(next, { value: next, label: created.term_name });
    onTermSelected?.(created);
    setModalOpen(false);
    createForm.resetFields();
    setSearch('');
  };

  const preventClose: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const searchTrimmed = search.trim();

  return (
    <>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        options={options}
        onSearch={setSearch}
        filterOption={(input, option) =>
          String(option?.label ?? option?.value ?? '')
            .toLowerCase()
            .includes(input.trim().toLowerCase())
        }
        notFoundContent={
          <div style={{ padding: '4px 8px' }}>
            <Text type="secondary">No matching payment term</Text>
            <div>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onMouseDown={preventClose}
                onClick={() => openCreate(searchTrimmed)}
              >
                {searchTrimmed ? `Create "${searchTrimmed}"` : 'Create new payment term'}
              </Button>
            </div>
          </div>
        }
        popupRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              disabled={disabled}
              onMouseDown={preventClose}
              onClick={() => openCreate(searchTrimmed)}
              style={{ width: '100%', textAlign: 'left' }}
            >
              Create new payment term
            </Button>
          </>
        )}
        {...rest}
      />
      <Modal
        title="New payment term"
        open={modalOpen}
        onOk={() => void handleCreate()}
        onCancel={() => setModalOpen(false)}
        confirmLoading={createMutation.isPending}
        destroyOnClose
        okText="Create and select"
        width={520}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="term_name"
            label="Term name"
            rules={[{ required: true, message: 'Required' }, { max: 100, message: 'Max 100 characters' }]}
          >
            <Input placeholder="e.g. Net 75, 40% advance / 60% delivery" />
          </Form.Item>
          <Form.Item
            name="days"
            label="Credit days"
            tooltip="Days until payment is due (0 for advance, COD, or milestone terms)"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Document text"
            tooltip="Wording stored on the quotation and shown on prints. Defaults to the term name if empty."
            rules={[{ max: 500, message: 'Max 500 characters' }]}
          >
            <Input.TextArea rows={3} placeholder="Optional full wording for the quotation" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PaymentTermSelect;
